import { defineStore } from "pinia";
import Vue from "vue";
import isEqual from "lodash/isEqual";
import SUBSCRIPTION_SETTINGS_QUERY from "@/graphql/subscription/subscriptionSettings.graphql";
import { useUserStore } from "~/stores/user";
import { requestInFlight, trackRequest } from "~/stores/user/inFlightRequests";

export const SUBSCRIPTION_SETTINGS_KEYS = [
  "goToHouse",
  "goToHouseKm",
  "city",
  "professions",
  "zones",
  "services",
  "zoneWaitingListSubscriptions",
];

const belongsTo = (data, me) =>
  !!data && !!me && String(data.id) === String(me.id);

const idsOf = (items) => items.map(({ id }) => ({ id }));

/**
 * The values of a subscription settings result that live on `me` (the
 * session set): `paymentType`, and the professions and zones as `{ id }`
 * lists.
 * @param {object} values
 * @returns {object}
 */
export function sessionValuesOf(values) {
  const session = {};
  if ("paymentType" in values) {
    session.paymentType = values.paymentType;
  }
  for (const key of ["professions", "zones"]) {
    if (Array.isArray(values[key])) {
      session[key] = idsOf(values[key]);
    }
  }
  return session;
}

/**
 * The person's subscription settings (`PersonSubscriptionSettingsFragment`):
 * the city, travel distance, professions, zones, services and zone waiting
 * lists, kept out of `me` and loaded where they are shown (the Profile card,
 * the subscription pages, the prices and references pages, the reference
 * photo upload, the designer's tender form).
 *
 * This store is the source of truth for these fields. `me` keeps only the
 * session copy of the professions' and zones' ids (for the navbar and the job
 * feed) and `paymentType`: every result this store receives, from its own
 * query or a mutation, updates them on `me` when they changed.
 *
 * Data is returned only while it belongs to the logged-in person.
 */
export const useSubscriptionSettingsStore = defineStore(
  "subscriptionSettingsStore",
  {
    state: () => ({
      loaded: null,
      pending: null,
    }),
    getters: {
      /**
       * The settings of the logged-in person, or null until they are loaded.
       * @returns {object | null}
       */
      data(state) {
        return belongsTo(state.loaded, useUserStore().me) ? state.loaded : null;
      },
      isLoaded() {
        return !!this.data;
      },
      city() {
        return this.data?.city ?? null;
      },
      goToHouse() {
        return this.data?.goToHouse ?? null;
      },
      goToHouseKm() {
        return this.data?.goToHouseKm ?? null;
      },
      professions() {
        return this.data?.professions ?? [];
      },
      zones() {
        return this.data?.zones ?? [];
      },
      services() {
        return this.data?.services ?? [];
      },
      zoneWaitingListSubscriptions() {
        return this.data?.zoneWaitingListSubscriptions ?? [];
      },
    },
    actions: {
      /**
       * Loads the settings once per person, without sending a second request
       * while one is in flight. Requests in flight, also a refresh of loaded
       * settings or one that replaced this call's own request, are waited
       * for, so the call never returns with data that is about to be
       * replaced or without the data a newer request brings.
       */
      async load() {
        if (!useUserStore().me) {
          return;
        }
        if (!this.data && !requestInFlight(this, "data", this.pending)) {
          await this.sendQuery();
        }
        let pending = requestInFlight(this, "data", this.pending);
        while (pending) {
          await pending.catch(() => {});
          pending = requestInFlight(this, "data", this.pending);
        }
      },

      /**
       * Reloads the settings. A response that arrives after a newer request
       * was sent, or after `setValues`, is dropped. A failed request keeps
       * the last data.
       */
      async refresh() {
        if (!useUserStore().me) {
          return;
        }
        await this.sendQuery();
      },

      /**
       * Applies a mutation result for the logged-in person: the settings
       * fields it selects go to the loaded settings, `paymentType` and the
       * professions' and zones' ids to `me`.
       * @param {object} values
       */
      setValues(values) {
        const me = useUserStore().me;
        if (!me || (values.id != null && String(values.id) !== String(me.id))) {
          return;
        }
        this.syncSession(values);
        const data = this.data;
        if (!data) {
          return;
        }
        this.pending = null;
        for (const key of SUBSCRIPTION_SETTINGS_KEYS) {
          if (key in values) {
            Vue.set(data, key, values[key]);
          }
        }
      },

      /**
       * Writes the session values of a result to `me` when they differ from
       * it, so the navbar and the job feed follow profession and zone changes.
       * @param {object} values
       */
      syncSession(values) {
        const userStore = useUserStore();
        const changed = Object.fromEntries(
          Object.entries(sessionValuesOf(values)).filter(
            ([key, value]) => !isEqual(userStore.me[key], value)
          )
        );
        if (Object.keys(changed).length > 0) {
          userStore.setUserValues(changed);
        }
      },

      async sendQuery() {
        const userStore = useUserStore();
        const query = this.$nuxt.$query(
          SUBSCRIPTION_SETTINGS_QUERY,
          {},
          { skipErrorPageOnAuthError: true }
        );
        const id = trackRequest(this, "data", query);
        this.pending = id;
        try {
          const result = await query;
          if (this.pending === id && belongsTo(result?.me, userStore.me)) {
            this.loaded = Object.fromEntries(
              ["id", ...SUBSCRIPTION_SETTINGS_KEYS].map((key) => [
                key,
                result.me[key],
              ])
            );
            this.syncSession(result.me);
          }
        } catch (error) {
          this.$nuxt.$logger.warn(
            "Failed to load the subscription settings",
            error
          );
        } finally {
          if (this.pending === id) {
            this.pending = null;
          }
        }
      },
    },
  }
);
